/**
 * Reusable client-direct S3 upload helper. Works with any upload surface that
 * follows the `presign → PUT (maybe multipart) → finalize` protocol. Callers
 * supply three callbacks wired to their own server endpoints and this helper
 * handles the file-to-S3 bytes, progress tracking, and part orchestration.
 *
 * Works unchanged against the dev-mode local PUT endpoint (no S3 configured)
 * because the server's presign returns a regular URL either way.
 */

export interface PresignSingle {
  kind: 'single'
  s3Key: string
  uploadUrl: string
  expiresInSeconds: number
}

export interface PresignMultipart {
  kind: 'multipart'
  s3Key: string
  uploadId: string
  /** Server-recommended chunk size. Client splits the file into pieces of this many bytes. */
  partSize: number
  expiresInSeconds: number
}

export type PresignResponse = PresignSingle | PresignMultipart

export interface UploadedPart {
  PartNumber: number
  ETag: string
}

/** Callbacks the caller wires to their server endpoints. */
export interface S3UploadFlow {
  presign(meta: { fileName: string; fileSize: number; contentType: string }): Promise<PresignResponse>
  /** Required only if the server may return a multipart presign. */
  presignPart?(s3Key: string, uploadId: string, partNumber: number): Promise<string>
  finalize(meta: {
    s3Key: string
    fileName: string
    fileSize: number
    contentType: string
    uploadId?: string
    parts?: UploadedPart[]
  }): Promise<void>
}

export interface S3UploadOpts {
  onProgress?: (pct: number) => void
  /** Concurrent part uploads during multipart. Default 4. */
  concurrency?: number
}

export async function uploadFileToS3(file: File, flow: S3UploadFlow, opts: S3UploadOpts = {}): Promise<void> {
  const contentType = file.type || 'application/octet-stream'
  const presign = await flow.presign({ fileName: file.name, fileSize: file.size, contentType })

  if (presign.kind === 'single') {
    await putWithProgress(presign.uploadUrl, file, contentType, (pct) => opts.onProgress?.(pct))
    await flow.finalize({
      s3Key: presign.s3Key,
      fileName: file.name,
      fileSize: file.size,
      contentType,
    })
    return
  }

  if (!flow.presignPart) {
    throw new Error('[s3-upload] server returned multipart presign but no presignPart callback was provided')
  }

  const parts = await uploadMultipart(file, contentType, presign, flow.presignPart, opts)
  await flow.finalize({
    s3Key: presign.s3Key,
    fileName: file.name,
    fileSize: file.size,
    contentType,
    uploadId: presign.uploadId,
    parts,
  })
}

async function uploadMultipart(
  file: File,
  contentType: string,
  presign: PresignMultipart,
  presignPart: NonNullable<S3UploadFlow['presignPart']>,
  opts: S3UploadOpts,
): Promise<UploadedPart[]> {
  const { partSize } = presign
  const totalParts = Math.ceil(file.size / partSize)
  const concurrency = Math.max(1, Math.min(opts.concurrency ?? 4, totalParts))
  const uploaded: UploadedPart[] = new Array(totalParts)
  const partProgress = new Array<number>(totalParts).fill(0)

  const reportProgress = (): void => {
    if (!opts.onProgress) {
      return
    }
    const bytesDone = partProgress.reduce((a, b) => a + b, 0)
    opts.onProgress(Math.min(100, Math.round((bytesDone / file.size) * 100)))
  }

  let nextIndex = 0
  async function worker(): Promise<void> {
    while (true) {
      const i = nextIndex++
      if (i >= totalParts) {
        return
      }
      const partNumber = i + 1
      const start = i * partSize
      const end = Math.min(start + partSize, file.size)
      const chunk = file.slice(start, end)
      const url = await presignPart(presign.s3Key, presign.uploadId, partNumber)
      const etag = await putPartWithProgress(url, chunk, contentType, (bytes) => {
        partProgress[i] = bytes
        reportProgress()
      })
      if (!etag) {
        // putPartWithProgress throws S3UploadError('etag-missing') before we reach here,
        // but belt-and-braces in case a future refactor changes that.
        throw new S3UploadError(
          `S3 returned no ETag for part ${partNumber} — check bucket CORS ExposeHeaders`,
          'etag-missing',
        )
      }
      uploaded[i] = { PartNumber: partNumber, ETag: etag }
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => worker()))
  return uploaded
}

/**
 * Distinguishable error shape so the UI layer can surface "your bucket CORS
 * policy is wrong" (which is what a `status === 0` failure on a cross-origin
 * PUT almost always means) differently from a real network outage.
 */
export class S3UploadError extends Error {
  constructor(
    message: string,
    readonly reason: 'cors' | 'http' | 'network' | 'etag-missing',
    readonly status?: number,
  ) {
    super(message)
    this.name = 'S3UploadError'
  }
}

const CORS_HINT =
  'Upload blocked before reaching S3 — likely bucket CORS. Verify PUT is allowed from this origin and ExposeHeaders includes ETag.'

/**
 * Extract S3's `<Code>` tag from an error response body. S3 always returns
 * XML like `<Error><Code>AccessDenied</Code><Message>...</Message></Error>`
 * on 4xx — capturing the Code turns a generic "403" into an actionable
 * diagnosis (`SignatureDoesNotMatch` vs `AccessDenied` vs `RequestTimeTooSkewed`,
 * etc.). Falls back to a 200-char body excerpt if we can't parse it.
 */
function extractS3ErrorCode(responseText: string): string {
  if (!responseText) {
    return ''
  }
  const match = /<Code>([^<]+)<\/Code>/.exec(responseText)
  if (match?.[1]) {
    return match[1]
  }
  return responseText.slice(0, 200).replace(/\s+/g, ' ').trim()
}

/** PUT a full file body with progress reporting. Resolves on 2xx, rejects otherwise. */
function putWithProgress(
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else if (xhr.status === 0) {
        // status 0 on a cross-origin XHR = preflight/CORS rejection or aborted connection.
        reject(new S3UploadError(CORS_HINT, 'cors', 0))
      } else {
        const code = extractS3ErrorCode(xhr.responseText)
        reject(new S3UploadError(`Upload failed (${xhr.status}${code ? ` ${code}` : ''})`, 'http', xhr.status))
      }
    }
    xhr.onerror = () => reject(new S3UploadError(CORS_HINT, 'cors', 0))
    xhr.send(body)
  })
}

/** PUT a single part; resolve with the ETag the server returns. */
function putPartWithProgress(
  url: string,
  body: Blob,
  contentType: string,
  onBytes: (bytes: number) => void,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onBytes(e.loaded)
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // S3 returns ETag with surrounding quotes; preserve them — CompleteMultipartUpload
        // wants the exact string back. `getResponseHeader` returns null unless the bucket's
        // CORS policy has `ExposeHeaders: ["ETag"]` — catch that explicitly.
        const etag = xhr.getResponseHeader('ETag')
        if (!etag) {
          reject(
            new S3UploadError(
              'Part upload succeeded but ETag is not readable. Add "ETag" to the bucket CORS ExposeHeaders.',
              'etag-missing',
              xhr.status,
            ),
          )
          return
        }
        resolve(etag)
      } else if (xhr.status === 0) {
        reject(new S3UploadError(CORS_HINT, 'cors', 0))
      } else {
        const code = extractS3ErrorCode(xhr.responseText)
        reject(new S3UploadError(`Part upload failed (${xhr.status}${code ? ` ${code}` : ''})`, 'http', xhr.status))
      }
    }
    xhr.onerror = () => reject(new S3UploadError(CORS_HINT, 'cors', 0))
    xhr.send(body)
  })
}
